import React from 'react'
import { css } from '@emotion/css'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  theme,
  IconCaretLeft,
  IconCaretRight
} from '@contrast-security-inc/design-system-foundations'
import { Tooltip } from '@contrast-security-inc/design-system-popovers'
import { Link, Text } from '@contrast-security-inc/design-system-typography'

interface Props {
  current: number
  total: number
  previous?: () => void
  next?: () => void
  previousSinkGroupTitle?: string
  nextSinkGroupTitle?: string
}

export function PagingNav({
  current,
  total,
  previous,
  next,
  previousSinkGroupTitle,
  nextSinkGroupTitle
}: Props) {
  return (
    <div
      data-testid="paging-nav"
      className={css`
        display: flex;
        align-items: center;
      `}
    >
      {!!previous && current > 1 && (
        <PagingControl
          sinkGroupTitle={previousSinkGroupTitle}
          type="previous"
          onClick={previous}
        />
      )}
      <FormattedMessage
        id="variable_currentCountOfTotalCountComponent"
        values={{
          current: <Text font="bodyStrong">{current}</Text>,
          total
        }}
      >
        {(...text) => {
          return (
            <div
              className={css`
                margin-bottom: 8px;
                padding: 0 8px;
              `}
            >
              {text.map((textItem, index) => {
                return (
                  <Text key={`message_${index}`} font="body">
                    {textItem}
                  </Text>
                )
              })}
            </div>
          )
        }}
      </FormattedMessage>
      {!!next && current < total && (
        <PagingControl
          type="next"
          sinkGroupTitle={nextSinkGroupTitle}
          onClick={next}
        />
      )}
    </div>
  )
}

const PagingControl = ({
  sinkGroupTitle,
  onClick,
  type
}: {
  sinkGroupTitle?: string
  onClick?: () => void
  type: 'next' | 'previous'
}) => {
  const { formatMessage } = useIntl()
  const Icon = type === 'next' ? IconCaretRight : IconCaretLeft
  return sinkGroupTitle ? (
    <Tooltip
      interactive={true}
      body={
        <div
          className={css`
            display: flex;
            flex-direction: column;
          `}
        >
          <Text>
            {formatMessage(
              { id: `detail_vulnerabilitySinkGroup_${type}` },
              { sinkGroupTitle }
            )}
          </Text>
          <Link onClick={onClick}>
            {formatMessage({ id: 'detail_vulnerabilitySinkGroup_link' })}
          </Link>
        </div>
      }
      className={css`
        z-index: 1002;
      `}
    >
      <div>
        <Icon size="md" color="neutral60" testId={`paging-nav-${type}`} />
      </div>
    </Tooltip>
  ) : (
    <div
      role="button"
      onClick={() => onClick && onClick()}
      className={css`
        cursor: pointer;
        & svg:hover {
          fill: ${theme.color.turquoise70};
        }
      `}
    >
      <Icon size="md" testId={`paging-nav-${type}`} />
    </div>
  )
}
